import type { ConfiguratorPageDefinition } from "~/features/configurator/ConfiguratorPageDefinition";
import { CARRIAGE_DEFINITION } from "~/features/configurator/Carriage/CarriageConfigurationPage";
import { KWLT_CARRIAGE_DEFINITION } from "~/features/configurator/Carriage/v2/configuration/kwlt/KWLTCarriageConfigurationPage";
import { RAIL_DEFINITION } from "~/features/configurator/Rail/v2/configuration/classic/RailConfigurationPage";
import { LC_DEFINITION } from "~/features/configurator/Linear/v2/configuration/classic/UnitConfiguratorPage";
import { KLLT_UNIT_DEFINITION } from "~/features/configurator/Linear/v2/configuration/kllt/KLLTUnitConfigurationPage";
import { KUEM_UNIT_DEFINITION } from "~/features/configurator/Linear/v2/configuration/kuem/KUEMUnitConfigurationPage";
import { METRIC_SHAFT_DEFINITION } from "~/features/configurator/Shaft/v2/configuration/MetricShaftConfiguratorPage";
import { IMPERIAL_SHAFT_DEFINITION } from "~/features/configurator/Shaft/v2/configuration/ImperialShaftConfiguratorPage";
import { TKDM_RAIL_DEFINITION } from "~/features/configurator/Rail/v2/configuration/tkdm/TKDMRailConfigurationPage";
import { TKLT_RAIL_DEFINITION } from "~/features/configurator/Rail/v2/configuration/tklt/TKLTRailConfigurationPage";
import type { ConfiguratorDefinition } from "~/features/configurator-v2";
import { BALL_SCREW_CONFIGURATOR_DEFINITION } from "~/features/configurator-v2/ballscrew";
import { EMA100_CONFIGURATOR_DEFINITION } from "~/features/configurator-v2/actuator";
import {
  BALL_SCREW_CONFIGURATOR_PRODUCT_CODE,
  EMA100_CONFIGURATOR_PRODUCT_CODE,
  TIMELINE_CONFIGURATOR_PRODUCT_CODES,
  type TimelineConfiguratorProductCode,
} from "~/domains/configurator/constants";

const LEGACY_CONFIGURATOR_DEFINITIONS = [
  CARRIAGE_DEFINITION,
  KWLT_CARRIAGE_DEFINITION,
  RAIL_DEFINITION,
  TKDM_RAIL_DEFINITION,
  TKLT_RAIL_DEFINITION,
  LC_DEFINITION,
  KLLT_UNIT_DEFINITION,
  KUEM_UNIT_DEFINITION,
  METRIC_SHAFT_DEFINITION,
  IMPERIAL_SHAFT_DEFINITION,
] as const;

export type ConfiguratorRegistration =
  | { engine: "legacy"; definition: ConfiguratorPageDefinition }
  | { engine: "timeline"; definition: ConfiguratorDefinition };

const TIMELINE_CONFIGURATOR_DEFINITIONS: Record<TimelineConfiguratorProductCode, ConfiguratorDefinition> = {
  [BALL_SCREW_CONFIGURATOR_PRODUCT_CODE]: BALL_SCREW_CONFIGURATOR_DEFINITION,
  [EMA100_CONFIGURATOR_PRODUCT_CODE]: EMA100_CONFIGURATOR_DEFINITION,
};

export const CONFIGURATOR_REGISTRATIONS: readonly ConfiguratorRegistration[] = [
  ...LEGACY_CONFIGURATOR_DEFINITIONS.map((definition) => ({ engine: "legacy" as const, definition })),
  ...TIMELINE_CONFIGURATOR_PRODUCT_CODES.map((productCode) => ({
    engine: "timeline" as const,
    definition: TIMELINE_CONFIGURATOR_DEFINITIONS[productCode],
  })),
];

export function findConfiguratorRegistration(productCode: string): ConfiguratorRegistration | undefined {
  return CONFIGURATOR_REGISTRATIONS.find((registration) => registration.definition.productCode === productCode);
}

export function getConfiguratorRegistration(productCode: string): ConfiguratorRegistration {
  const registration = findConfiguratorRegistration(productCode);
  if (!registration) {
    throw new Error(`No configurator registered for productCode: ${productCode}`);
  }
  return registration;
}

export function getConfiguratorDefinition(productCode: string): ConfiguratorPageDefinition {
  const definition = LEGACY_CONFIGURATOR_DEFINITIONS.find((candidate) => candidate.productCode === productCode);
  if (!definition) {
    throw new Error(`No configurator definition registered for productCode: ${productCode}`);
  }
  return definition;
}

export function getAllConfiguratorDefinitions(): readonly ConfiguratorPageDefinition[] {
  return LEGACY_CONFIGURATOR_DEFINITIONS;
}
